import clsx from 'clsx';
import { GlobalStyles, List, styled } from '@mui/material';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import SchoolIcon from '@mui/icons-material/School';
import SummarizeIcon from '@mui/icons-material/Summarize';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import CalculateIcon from '@mui/icons-material/Calculate';
import NavGroup from './NavGroup';
import NavLink from './NavItem';
import { useMeCore } from '../../core/modules/me';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import { useCourseCore } from '../../core/modules/course';
import ConstructionIcon from '@mui/icons-material/Construction';
import ForumIcon from '@mui/icons-material/Forum';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ClassIcon from '@mui/icons-material/Class';
import HandshakeIcon from '@mui/icons-material/Handshake';
import CampaignIcon from '@mui/icons-material/Campaign';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import ScienceIcon from '@mui/icons-material/Science';
import NavItemWithSubmenu from './NavItemWithSubmenu';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountBalance from '@mui/icons-material/AccountBalance';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import {
  CLUB_DE_TRADING_HUB_URL,
  MARATON_DE_INVERSIONES_PORTAFOLIOS_URL,
  isClubDeTradingCourse,
  isMaratonDeInversionesCourse,
} from '../../core/constants/courses';

interface Props {
  className?: string;
}

const inputGlobalStyles = (
  <GlobalStyles
    styles={() => ({
      '.popper-navigation-list': {
        '& .fuse-list-item': {
          padding: '8px 12px 8px 12px',
          height: 40,
          minHeight: 40,
          '& .fuse-list-item-text': {
            padding: '0 0 0 8px',
          },
        },
        '&.dense': {
          '& .fuse-list-item': {
            minHeight: 32,
            height: 32,
            '& .fuse-list-item-text': {
              padding: '0 0 0 8px',
            },
          },
        },
      },
    })}
  />
);

const StyledList = styled(List)(({ theme }) => ({
  '& .fuse-list-item': {
    '&:hover': {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.05)'
          : 'rgba(0,0,0,.04)',
    },
    '&:focus:not(.active)': {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.06)'
          : 'rgba(0,0,0,.05)',
    },
    padding: '8px 12px 8px 12px',
    height: 40,
    minHeight: 40,
    '&.level-0': {
      minHeight: 44,
      minminHeight: 44,
    },
    '& .fuse-list-item-text': {
      padding: '0 0 0 8px',
    },
  },
  '& .fuse-list-item.active, & .fuse-list-item.submenu-item.active': {
    backgroundColor: 'rgba(255,255,255,0.08) !important',
    color: 'inherit',
  },
  '&.active-square-list': {
    '& .fuse-list-item': {
      borderRadius: '0',
    },
  },
}));

const Navigation = (props: Props) => {
  const { t } = useTranslation();

  const { className } = props;
  const { user } = useMeCore();
  const { course, currentClassId } = useCourseCore();

  const showAdminDashboard = useMemo(() => user?.userRole.admin, [user?.userRole]);
  const showAutomationDashboard = useMemo(() =>
    user?.userRole.admin || user?.userRole.automation,
    [user?.userRole]);
  const showMeCoursesDashboard = useMemo(() => !user?.userRole.automation, [user?.userRole]);

  return (
    <>
      {inputGlobalStyles}
      <StyledList
        className={clsx(
          'navigation whitespace-nowrap items-center flex flex-col px-12',
          className
        )}
      >
        {showMeCoursesDashboard && (
          <NavLink
            title={t('sidebar.link.mycourses')}
            url="/dashboard/me/courses"
            icon={<AssignmentTurnedInOutlinedIcon />}
          />
        )}
        {course && (
          <>
            <NavGroup title={course.name} subtitle="Herramientas">
              <NavLink
                title="Clases"
                url={`/dashboard/me/course/${course.id}/classes?classId=${currentClassId}`}
                icon={<ClassIcon />}
              />
              {course.forum && (
                <NavLink
                  title="Foro"
                  url={`/dashboard/me/course/${course.id}/forum`}
                  icon={<ForumIcon />}
                />
              )}
              {course.calendar && (
                <NavLink
                  title="Calendario"
                  url={`/dashboard/me/course/${course.id}/calendar`}
                  icon={<CalendarMonthIcon />}
                />
              )}
              {course.report && (
                <NavLink
                  title="Reportes"
                  url={`/dashboard/me/course/${course.id}/reports`}
                  icon={<SummarizeIcon />}
                />
              )}
              {course.alliance && (
                <NavLink
                  title="Alianzas"
                  url={`/dashboard/me/course/${course.id}/alliance`}
                  icon={<HandshakeIcon />}
                />
              )}
            </NavGroup>
            {(course.positionCalculator ||
              course.diagnosticAnswers ||
              (course as any).financialMetrics ||
              course.tradingMetrics ||
              (course as any).liquidAssets ||
              (course as any).realEstate ||
              (course as any).financialGoals ||
              isClubDeTradingCourse(course) ||
              isMaratonDeInversionesCourse(course)) && (
              <NavItemWithSubmenu
                title="Herramientas"
                icon={<ScienceIcon />}
                submenu={[
                  ...(isMaratonDeInversionesCourse(course) ? [{
                    title: 'Portafolios',
                    url: MARATON_DE_INVERSIONES_PORTAFOLIOS_URL,
                    icon: <ShowChartIcon />,
                    external: true,
                  }] : []),
                  ...(isClubDeTradingCourse(course) ? [{
                    title: 'Club de Trading HUB',
                    url: CLUB_DE_TRADING_HUB_URL,
                    icon: <ShowChartIcon />,
                    external: true,
                  }] : []),
                  ...(course.positionCalculator ? [{
                    title: 'Calculadora de Posición',
                    url: `/dashboard/me/course/${course.id}/position-calculator`,
                    icon: <CalculateIcon />
                  }] : []),
                  ...(course.diagnosticAnswers ? [{
                    title: 'Preguntas Diagnóstico',
                    url: `/dashboard/me/course/${course.id}/diagnostic-questions`,
                    icon: <AssessmentIcon />
                  }] : []),
                  ...((course as any).financialMetrics ? [{
                    title: 'Métricas Financieras',
                    url: `/dashboard/me/course/${course.id}/financial-metrics`,
                    icon: <AccountBalance />
                  }] : []),
                  ...(course.tradingMetrics ? [{
                    title: 'Métricas Trading',
                    url: `/dashboard/me/course/${course.id}/trading-metrics`,
                    icon: <AssessmentIcon />
                  }] : []),
                  ...((course as any).liquidAssets ? [{
                    title: 'Activos Líquidos',
                    url: `/dashboard/me/course/${course.id}/liquid-assets`,
                    icon: <AccountBalanceWalletIcon />
                  }] : []),
                  ...((course as any).realEstate ? [{
                    title: 'Departamentos',
                    url: `/dashboard/me/course/${course.id}/real-estate`,
                    icon: <HomeWorkIcon />
                  }] : []),
                  ...((course as any).financialGoals ? [{
                    title: 'Metas Financieras',
                    url: `/dashboard/me/course/${course.id}/financial-goals`,
                    icon: <TrendingUpIcon />
                  }] : []),
                ]}
              />
            )}
          </>
        )}
        {showAutomationDashboard && (
          <NavGroup title={t('sidebar.menu.automation.title')} subtitle={t('sidebar.menu.automation.subtitle')}>
            <NavLink
              title={t('sidebar.link.report')}
              url="/dashboard/automation/reports"
              icon={<SummarizeIcon />}
            />
            <NavLink
              title={t('sidebar.link.subscription')}
              url="/dashboard/automation/subscriptions"
              icon={<SubscriptionsIcon />}
            />
          </NavGroup>
        )}
        {showAdminDashboard && (
          <NavGroup title={t('sidebar.menu.admin.title')} subtitle={t('sidebar.menu.admin.subtitle')}>
            <NavLink
              title={t('sidebar.link.users')}
              url="/dashboard/users"
              icon={<GroupOutlinedIcon />}
            />
            <NavLink
              title={t('sidebar.link.courses')}
              url="/dashboard/courses"
              icon={<SchoolIcon />}
            />
            <NavLink
              title="Plantillas"
              url="/dashboard/templates"
              icon={<EmailOutlinedIcon />}
            />
            <NavLink
              title="Secuencia de Mails"
              url="/dashboard/mailSecuence"
              icon={<SettingsSuggestIcon />}
            />
            <NavLink
              title="Respuestas Alumnos"
              url="/dashboard/tools"
              icon={<FactCheckIcon />}
            />
            <NavLink
              title="Comunicados"
              url="/dashboard/announcements"
              icon={<CampaignIcon />}
            />
          </NavGroup>
        )}
      </StyledList>
    </>
  );
};

export default Navigation;
